import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import Loading from "components/Loading";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { useFdeRole } from "hooks/useFdeRole";
import { listTciRegistry } from "services/fde/fdeRegistryService";
import {
  AUTONOMY_POSTURES,
  REVIEW_CADENCES,
  collectInferenceNodes,
  collectPendingByPromptId,
  getTciGovernanceProfile,
  updateCareStartPlusModuleStatus,
  updateVisitStartElementGovernance,
} from "services/fde/tciGovernanceService";
import {
  VISIT_START_TCI_ID,
  buildEnabledElements,
  filterElementGovernance,
  getVisitStartConfigurableElementIds,
  getVisitStartConfigurableElements,
  getVisitStartCoreElements,
  resolveEnabledElements,
} from "services/fde/visitStartElementsCatalog";
import { loadManagedDomains, PENDING_CANDIDATES_LIMIT } from "services/fde/tciManagerService";
import { HELP_VIDEO_WORKFLOWS } from "services/helpVideoService";
import { useRegisterHelpWorkflow } from "hooks/useRegisterHelpWorkflow";
import { db } from "services/firebase";
import PromptGraphDiagram from "./PromptGraphDiagram";
import PromptGraphSidePanel from "./PromptGraphSidePanel";
import TciSharingControl from "components/fde/TciSharingControl";

const ManageTcisPage = () => {
  useRegisterHelpWorkflow(HELP_VIDEO_WORKFLOWS.TCI_MANAGER_MANAGE_TCIS);
  const navigate = useNavigate();
  const { tciId: routeTciId } = useParams();
  const { user, isGlobalAdmin, userData, organizationId } = useAuth();
  const { isFdeDc, loading: fdeLoading } = useFdeRole();
  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    null;
  const isInstitutionAdmin = Boolean(institutionId) && userData?.role === "admin";

  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [managedTciIds, setManagedTciIds] = useState([]);
  const [tciNameById, setTciNameById] = useState({});
  const [tciById, setTciById] = useState({});
  const [profilesByTciId, setProfilesByTciId] = useState({});
  const [pendingCandidates, setPendingCandidates] = useState([]);
  const [pendingCandidatesTruncated, setPendingCandidatesTruncated] = useState(false);
  const [selectedTciId, setSelectedTciId] = useState(routeTciId || "");
  const [panelNode, setPanelNode] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [moduleToggleBusyId, setModuleToggleBusyId] = useState("");
  const [moduleToggleError, setModuleToggleError] = useState("");
  const [moduleToggleSuccess, setModuleToggleSuccess] = useState("");
  const [visitStartElementBusyId, setVisitStartElementBusyId] = useState("");
  const [visitStartElementError, setVisitStartElementError] = useState("");
  const [visitStartElementSuccess, setVisitStartElementSuccess] = useState("");

  const canAccess = isFdeDc || isGlobalAdmin || isInstitutionAdmin;

  const loadData = useCallback(async () => {
    if (!user?.uid || !institutionId || !canAccess) {
      return;
    }
    setLoading(true);
    setErrorMessage("");
    try {
      const registry = await listTciRegistry({ institutionId });
      const domains =
        isFdeDc || isGlobalAdmin
          ? await loadManagedDomains({ uid: user.uid, institutionId })
          : (
              await db
                .collection("clinicalDomains")
                .where("institutionId", "==", institutionId)
                .where("isActive", "==", true)
                .get()
            ).docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      let pendingCandidates = [];
      let pendingTruncated = false;
      try {
        // Institution-wide by design (badge counts across governed TCIs), but capped
        // like tciManagerService PENDING_CANDIDATES_LIMIT — never unbounded.
        const pendingSnap = await db
          .collection("promptCandidates")
          .where("institutionId", "==", institutionId)
          .where("status", "==", "pending")
          .limit(PENDING_CANDIDATES_LIMIT)
          .get();
        pendingCandidates = pendingSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        pendingTruncated = pendingSnap.size >= PENDING_CANDIDATES_LIMIT;
      } catch (pendingErr) {
        console.warn("[ManageTcisPage] promptCandidates load failed", pendingErr);
      }

      let tciIds = [
        ...new Set(domains.flatMap((domain) => domain.tciIds || []).filter(Boolean)),
      ];
      if (routeTciId && !tciIds.includes(routeTciId)) {
        const routeTci = registry.find((row) => row.id === routeTciId);
        if (routeTci?.scope === "institutional") {
          tciIds.push(routeTciId);
        }
      }
      const names = Object.fromEntries(registry.map((row) => [row.id, row.name || row.id]));
      const byId = Object.fromEntries(registry.map((row) => [row.id, row]));

      const profiles = {};
      await Promise.all(
        tciIds.map(async (tciId) => {
          profiles[tciId] = await getTciGovernanceProfile({ institutionId, tciId });
        }),
      );

      setManagedTciIds(tciIds);
      setTciNameById(names);
      setTciById(byId);
      setProfilesByTciId(profiles);
      setPendingCandidates(pendingCandidates);
      setPendingCandidatesTruncated(pendingTruncated);

      if (!routeTciId && tciIds.length === 1) {
        setSelectedTciId(tciIds[0]);
      } else if (routeTciId) {
        setSelectedTciId(routeTciId);
      }
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load TCI graph data.");
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [user?.uid, institutionId, canAccess, routeTciId]);

  useEffect(() => {
    if (!fdeLoading) loadData();
  }, [loadData, fdeLoading]);

  useEffect(() => {
    if (routeTciId) setSelectedTciId(routeTciId);
  }, [routeTciId]);

  const handleSelectTci = (tciId) => {
    setSelectedTciId(tciId);
    setPanelNode(null);
    navigate(`/dashboard/tci-manager/tcis/${encodeURIComponent(tciId)}`, { replace: !routeTciId });
  };

  const selectedProfile = profilesByTciId[selectedTciId];
  const selectedTci = tciById[selectedTciId];
  const selectedGraph = selectedProfile?.promptGraph || [];

  const pendingByPromptId = useMemo(
    () => collectPendingByPromptId(pendingCandidates, selectedGraph),
    [pendingCandidates, selectedGraph],
  );

  const pendingCountByTciId = useMemo(() => {
    const counts = {};
    managedTciIds.forEach((tciId) => {
      const graph = profilesByTciId[tciId]?.promptGraph || [];
      const inferenceIds = new Set(collectInferenceNodes(graph).map((row) => row.promptId));
      counts[tciId] = pendingCandidates.filter(
        (row) => row.status === "pending" && inferenceIds.has(row.promptId),
      ).length;
    });
    return counts;
  }, [managedTciIds, profilesByTciId, pendingCandidates]);

  const postureLabel = useMemo(() => {
    const match = AUTONOMY_POSTURES.find(
      (row) => row.value === selectedProfile?.autonomyPosture,
    );
    return match?.label || selectedProfile?.autonomyPosture;
  }, [selectedProfile?.autonomyPosture]);

  const cadenceLabel = useMemo(() => {
    const match = REVIEW_CADENCES.find((row) => row.value === selectedProfile?.reviewCadence);
    return match?.label || selectedProfile?.reviewCadence;
  }, [selectedProfile?.reviewCadence]);

  const careStartPlusModules = useMemo(
    () =>
      managedTciIds
        .filter((tciId) => tciById[tciId]?.careStartPlusEligible === true)
        .map((tciId) => ({
          tciId,
          name: tciNameById[tciId] || tciId,
          enabled: profilesByTciId[tciId]?.careStartPlusEnabled === true,
        })),
    [managedTciIds, tciById, tciNameById, profilesByTciId],
  );

  const handleCareStartPlusToggle = async (tciId, enabled) => {
    if (!institutionId || !tciId) return;
    setModuleToggleBusyId(tciId);
    setModuleToggleError("");
    setModuleToggleSuccess("");
    try {
      await updateCareStartPlusModuleStatus({
        institutionId,
        tciId,
        careStartPlusEnabled: enabled,
      });
      setProfilesByTciId((prev) => ({
        ...prev,
        [tciId]: {
          ...(prev[tciId] || { institutionId, tciId }),
          careStartPlusEnabled: enabled,
        },
      }));
      setModuleToggleSuccess(
        enabled
          ? `${tciNameById[tciId] || tciId} enabled for CareStart-Plus.`
          : `${tciNameById[tciId] || tciId} disabled for CareStart-Plus.`,
      );
    } catch (err) {
      setModuleToggleError(err?.message || "Failed to update CareStart-Plus module.");
    } finally {
      setModuleToggleBusyId("");
    }
  };

  const visitStartCoreElements = useMemo(() => getVisitStartCoreElements(), []);
  const visitStartConfigurableElements = useMemo(
    () =>
      getVisitStartConfigurableElements().filter(
        (element) => element.category === "clinical",
      ),
    [],
  );
  const visitStartEnabledElements = useMemo(() => {
    if (selectedTciId !== VISIT_START_TCI_ID) return [];
    return resolveEnabledElements(profilesByTciId[VISIT_START_TCI_ID]);
  }, [selectedTciId, profilesByTciId]);

  const handleVisitStartElementToggle = async (elementId, enabled) => {
    if (!institutionId || selectedTciId !== VISIT_START_TCI_ID) return;
    const profile = profilesByTciId[VISIT_START_TCI_ID] || {};
    const currentEnabled = resolveEnabledElements(profile);
    const configurableOn = new Set(
      currentEnabled.filter((id) =>
        getVisitStartConfigurableElementIds().includes(id),
      ),
    );
    if (enabled) configurableOn.add(elementId);
    else configurableOn.delete(elementId);

    const nextEnabled = buildEnabledElements([...configurableOn]);
    const nextGovernance = filterElementGovernance(profile.elementGovernance, nextEnabled);

    setVisitStartElementBusyId(elementId);
    setVisitStartElementError("");
    setVisitStartElementSuccess("");
    try {
      await updateVisitStartElementGovernance({
        institutionId,
        enabledElements: nextEnabled,
        elementGovernance: nextGovernance,
      });
      setProfilesByTciId((prev) => ({
        ...prev,
        [VISIT_START_TCI_ID]: {
          ...(prev[VISIT_START_TCI_ID] || { institutionId, tciId: VISIT_START_TCI_ID }),
          enabledElements: nextEnabled,
          elementGovernance: nextGovernance,
        },
      }));
      const label =
        visitStartConfigurableElements.find((row) => row.elementId === elementId)?.label ||
        elementId;
      setVisitStartElementSuccess(
        enabled ? `${label} enabled for VisitStart.` : `${label} disabled for VisitStart.`,
      );
    } catch (err) {
      setVisitStartElementError(err?.message || "Failed to update VisitStart elements.");
    } finally {
      setVisitStartElementBusyId("");
    }
  };

  if (fdeLoading || (canAccess && !institutionId) || (canAccess && !hasLoaded) || loading) {
    return <Loading page />;
  }

  if (!canAccess) {
    return (
      <Alert severity="warning">
        Manage TCIs is available to TCI Manager role holders.
      </Alert>
    );
  }

  if (hasLoaded && !managedTciIds.length) {
    return (
      <Box>
        <DashboardPageHeader
          title="Manage TCIs"
          subtitle="Prompt structure for TCIs you govern."
        />
        <Alert severity="info" sx={{ mt: 2 }}>
          No clinical domains are assigned to you yet. Ask your institution admin to assign you
          as TCI Manager on a domain.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <DashboardPageHeader
        title="Manage TCIs"
        subtitle="View the prompt structure for each TCI you govern."
      />

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}
      {pendingCandidatesTruncated ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing the first {PENDING_CANDIDATES_LIMIT} pending prompt candidates for this
          institution. Review or resolve some before additional items appear in this list.
        </Alert>
      ) : null}

      {careStartPlusModules.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
            CareStart-Plus modules
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Choose which patient-facing intake TCIs are active modules for CareStart-Plus at
            this institution.
          </Typography>
          {moduleToggleError && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setModuleToggleError("")}>
              {moduleToggleError}
            </Alert>
          )}
          {moduleToggleSuccess && (
            <Alert
              severity="success"
              sx={{ mb: 1.5 }}
              onClose={() => setModuleToggleSuccess("")}
            >
              {moduleToggleSuccess}
            </Alert>
          )}
          <Stack spacing={0.5}>
            {careStartPlusModules.map((row) => (
              <Box
                key={row.tciId}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={row.enabled}
                      onChange={(event) =>
                        handleCareStartPlusToggle(row.tciId, event.target.checked)
                      }
                      disabled={Boolean(moduleToggleBusyId)}
                    />
                  }
                  label={row.name}
                />
                {moduleToggleBusyId === row.tciId ? (
                  <CircularProgress size={18} />
                ) : null}
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {selectedTciId === VISIT_START_TCI_ID && (
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 2 }}
          data-testid="visit-start-elements-panel"
        >
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
            VisitStart elements
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Turn configurable clinical intake elements on or off for this institution.
            Core elements stay active and cannot be disabled. Administrative
            elements (eligibility, referral, payment, dependents) are governed by
            Clinic Manager (Administrative).
          </Typography>
          {visitStartElementError && (
            <Alert
              severity="error"
              sx={{ mb: 1.5 }}
              onClose={() => setVisitStartElementError("")}
            >
              {visitStartElementError}
            </Alert>
          )}
          {visitStartElementSuccess && (
            <Alert
              severity="success"
              sx={{ mb: 1.5 }}
              onClose={() => setVisitStartElementSuccess("")}
            >
              {visitStartElementSuccess}
            </Alert>
          )}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1 }}
            data-testid="visit-start-core-elements"
          >
            Always active:{" "}
            {visitStartCoreElements.map((row) => row.label).join("; ")}.
          </Typography>
          <Stack spacing={0.5}>
            {visitStartConfigurableElements.map((row) => {
              const checked = visitStartEnabledElements.includes(row.elementId);
              return (
                <Box
                  key={row.elementId}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={checked}
                        onChange={(event) =>
                          handleVisitStartElementToggle(row.elementId, event.target.checked)
                        }
                        disabled={Boolean(visitStartElementBusyId)}
                        inputProps={{
                          "data-testid": `visit-start-element-${row.elementId}`,
                        }}
                      />
                    }
                    label={row.label}
                  />
                  {visitStartElementBusyId === row.elementId ? (
                    <CircularProgress size={18} />
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        </Paper>
      )}

      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", minHeight: 480 }}>
        <Paper variant="outlined" sx={{ width: 240, flexShrink: 0 }}>
          <List dense disablePadding>
            {managedTciIds.map((tciId) => {
              const pendingCount = pendingCountByTciId[tciId] || 0;
              return (
                <ListItemButton
                  key={tciId}
                  selected={selectedTciId === tciId}
                  onClick={() => handleSelectTci(tciId)}
                >
                  <ListItemText
                    primary={tciNameById[tciId] || tciId}
                    secondary={pendingCount ? `${pendingCount} pending` : null}
                  />
                  {pendingCount > 0 && (
                    <Chip size="small" color="error" label={pendingCount} sx={{ ml: 1 }} />
                  )}
                </ListItemButton>
              );
            })}
          </List>
        </Paper>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!selectedTciId ? (
            <Typography color="text.secondary">Select a TCI to view its prompt structure.</Typography>
          ) : !selectedGraph.length ? (
            <Alert severity="info">
              No prompt structure has been defined for this TCI yet. Add nodes via the{" "}
              <RouterLink to={`/dashboard/tci-governance/${encodeURIComponent(selectedTciId)}`}>
                TCI Governance profile
              </RouterLink>
              .
            </Alert>
          ) : (
            <PromptGraphDiagram
              graph={selectedGraph}
              tciId={selectedTciId}
              pendingByPromptId={pendingByPromptId}
              onNodePanelOpen={setPanelNode}
            />
          )}
        </Box>

        {panelNode && (
          <PromptGraphSidePanel
            node={panelNode}
            graph={selectedGraph}
            onClose={() => setPanelNode(null)}
          />
        )}

        {selectedTciId && !panelNode && (
          <Collapse in={summaryOpen} orientation="horizontal">
            <Paper variant="outlined" sx={{ width: 300, flexShrink: 0, p: 2 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle1">
                  {tciNameById[selectedTciId] || selectedTciId}
                </Typography>
                {selectedProfile ? (
                  <>
                    <Chip
                      size="small"
                      label={`Posture: ${postureLabel || "—"}`}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={`Cadence: ${cadenceLabel || "—"}`}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={selectedProfile.status || "configuring"}
                      variant="outlined"
                    />
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No governance profile yet.
                  </Typography>
                )}
                <Typography
                  component={RouterLink}
                  to={`/dashboard/tci-governance/${encodeURIComponent(selectedTciId)}`}
                  variant="body2"
                  color="primary"
                >
                  Open TCI governance →
                </Typography>
                {selectedTci?.scope === "institutional" ? (
                  <TciSharingControl tci={selectedTci} onUpdated={loadData} />
                ) : null}
              </Stack>
            </Paper>
          </Collapse>
        )}

        {selectedTciId && !panelNode && (
          <IconButton
            size="small"
            onClick={() => setSummaryOpen((open) => !open)}
            sx={{ alignSelf: "flex-start" }}
            aria-label={summaryOpen ? "Hide summary" : "Show summary"}
          >
            {summaryOpen ? <ChevronRightRounded /> : <ChevronLeftRounded />}
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default ManageTcisPage;
